import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteParams } from '../../hooks/useRouteParams';
import {
  open as whatIfsOpenSelector,
  planImpactLoadingSelector,
  visible as whatIfsVisibleSelector,
} from '../../../../WhatIfs/WhatIfsSelector';
import {
  setCurrentViewId,
  setOpen as setWhatIfsOpen,
  setVisible as setWhatIfsVisible,
} from '../../../../WhatIfs/WhatIfsActions';
import { setGoalId } from '../../../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownActions';
import { useIsCurrentPlanSelected } from '../../hooks/useIsCurrentPlanSelected';
import { useGoal } from '../../hooks/useGoal';
import { EstatePlanningGoalTabs } from '../../GoalPage.constants';
import { GoalTypeDTO } from '../../../types/goalsDTO';
import { EXTERNAL_HOST } from '../../../../ConsumerArea/ConsumerAreaConstants';
import { externalHostSelector as selectExternalHostSelector } from '../../../../ConsumerArea/ConsumerAreaSelector';

interface UseHeaderButtons {
  data: {
    showWhatIfs: boolean;
    showAddGoal: boolean;
    isWhatIfsOpen: boolean;
    isPlanImpactLoading: boolean;
  };
  actions: {
    openWhatIfs(): void;
    handleAddGoal(): void;
  };
}

export const useHeaderButtons = (): UseHeaderButtons => {
  const dispatch = useDispatch();

  const { clientId, goalId, tab } = useRouteParams();

  const { data: goal } = useGoal(goalId);

  const { isCurrentPlanSelected } = useIsCurrentPlanSelected();

  const externalHost = useSelector(selectExternalHostSelector);

  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const isWhatIfsOpen = useSelector(state => whatIfsOpenSelector(state, { clientId }));
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const isWhatIfsVisible = useSelector(state => whatIfsVisibleSelector(state, { clientId }));
  const hasAccessToWhatIfs = ADV.entities.isOptedIn('Calibration');

  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  const isPlanImpactLoading = useSelector(state => planImpactLoadingSelector(state, { clientId }));

  useEffect(() => {
    dispatch(setWhatIfsVisible(clientId, true));
    dispatch(setGoalId(goalId, clientId));
    dispatch(setCurrentViewId(clientId, goalId, null));
  }, [clientId, dispatch, goalId]);

  const isWhatIfsAccessible =
    hasAccessToWhatIfs &&
    (!isWhatIfsOpen || !isWhatIfsVisible) &&
    !isCurrentPlanSelected &&
    goal?.AllowCashflowCalibration;

  const showWhatIfs =
    externalHost !== EXTERNAL_HOST.ELEMENT && goal && goal.GoalType === GoalTypeDTO.EstatePlanning
      ? tab === EstatePlanningGoalTabs.EstateFlow
        ? isWhatIfsAccessible
        : false
      : isWhatIfsAccessible;

  const showAddGoal = ADV.entities.isOptedIn('EditFinancialPlan');

  const openWhatIfs = () => {
    dispatch(setWhatIfsOpen(clientId, true));
  };

  const handleAddGoal = () => {
    // Until we migrate all goals to OPL, we will have to close what ifs here
    // After migration we should close the "what ifs" on GoalPage unMount.
    dispatch(setWhatIfsOpen(clientId, false));

    ADV.util.Router.go2('ClientEditPlan', {
      clientId,
    });
  };

  return {
    data: {
      showWhatIfs,
      showAddGoal,
      isWhatIfsOpen,
      isPlanImpactLoading,
    },

    actions: {
      openWhatIfs,
      handleAddGoal,
    },
  };
};
